// ============================================================================
// dataTypes/liveStreamManagment/sourceRegistry/acquirers/mediaElementCapture.ts
//
// Capture d'un <video> en MediaStream, avec un repli pour Safari.
//
// Safari n'a pas HTMLMediaElement.captureStream() (relevé le 29/09/2026 : la
// régie ouverte sous Safari 27 échouait en boucle sur « captureStream() non
// supporté », retour monitor et sources HLS inutilisables). Il a en revanche
// canvas.captureStream(), que le reste de l'application utilise déjà.
//
// Repli quand la capture native est absente :
//   image  <video> → drawImage sur un canvas → canvas.captureStream()
//   son    <video> → createMediaElementSource → MediaStreamAudioDestinationNode
//                    (jamais relié aux haut-parleurs : la source est tirée du
//                    <video>, qui ne sort donc plus rien de lui-même)
//
// ⚠️ Deux limites que le code ne peut pas lever :
//   - l'AudioContext reste suspendu tant qu'aucun geste utilisateur ne l'a
//     repris : la piste audio existe mais porte du silence jusque-là ;
//   - onglet occulté, Safari ralentit les minuteries et peut cesser de tirer
//     requestVideoFrameCallback : le canvas gèle en arrière-plan.
//
// ⚠️ Dans ce mode l'élément NE DOIT PAS rester muet : un élément muet envoie du
// silence dans le graphe Web Audio. Les appelants s'en remettent à
// `capturesViaWebAudio()` pour savoir s'ils ont le droit de le remuter.
// ============================================================================

import { registerProbeGauge } from '../../resourceProbe';

const FALLBACK_FPS = 25;

/** Les encodeurs vidéo veulent des dimensions paires. */
const pair = (n: number): number => (n % 2 === 0 ? n : n - 1);

interface CanvasCapture {
  canvas       : HTMLCanvasElement;
  stopLoop     : () => void;
  audioSource  : MediaElementAudioSourceNode | null;
  audioDest    : MediaStreamAudioDestinationNode | null;
}

const captures = new WeakMap<HTMLVideoElement, CanvasCapture>();

// 🔍 DIAGNOSTIC 2026-10-23 (cf. resourceProbe.ts) : captures canvas encore actives.
let liveCaptures = 0;
registerProbeGauge('captures canvas', () => liveCaptures);

let sharedCtx: AudioContext | null = null;

const getAudioContext = (): AudioContext | null => {
  try {
    if (!sharedCtx || sharedCtx.state === 'closed') {
      sharedCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      // Le premier geste utilisateur reprend le contexte (autoplay Safari).
      const resume = () => {
        sharedCtx?.resume().catch(() => {});
        window.removeEventListener('pointerdown', resume, true);
        window.removeEventListener('keydown',     resume, true);
      };
      window.addEventListener('pointerdown', resume, true);
      window.addEventListener('keydown',     resume, true);
    }
    if (sharedCtx.state === 'suspended') sharedCtx.resume().catch(() => {});
    return sharedCtx;
  } catch {
    return null;
  }
};

const hasNativeCapture = (video: HTMLVideoElement): boolean =>
  typeof (video as any).captureStream === 'function' ||
  typeof (video as any).mozCaptureStream === 'function';

/** Vrai si cet élément est (ou sera) capturé par le repli canvas + Web Audio. */
export const capturesViaWebAudio = (video: HTMLVideoElement): boolean =>
  captures.has(video) || !hasNativeCapture(video);

const startCanvas = (video: HTMLVideoElement): CanvasCapture | null => {
  const width  = pair(video.videoWidth);
  const height = pair(video.videoHeight);
  if (!width || !height) return null;   // rien décodé : une piste vivante et muette serait pire qu'un échec

  const canvas  = document.createElement('canvas');
  canvas.width  = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  let alive = true;
  const draw = () => {
    if (!alive) return;
    if (video.videoWidth > 0 && video.videoHeight > 0) {
      ctx.drawImage(video, 0, 0, width, height);
    }
  };

  const rvfc   = (video as any).requestVideoFrameCallback;
  let frameId  = 0;
  let timer: ReturnType<typeof setInterval> | null = null;
  if (typeof rvfc === 'function') {
    const loop = () => {
      if (!alive) return;
      draw();
      frameId = rvfc.call(video, loop);
    };
    frameId = rvfc.call(video, loop);
  } else {
    timer = setInterval(draw, 1000 / FALLBACK_FPS);
  }
  draw();

  return {
    canvas,
    audioSource: null,
    audioDest  : null,
    stopLoop   : () => {
      alive = false;
      if (timer) clearInterval(timer);
      const cancel = (video as any).cancelVideoFrameCallback;
      if (typeof cancel === 'function' && frameId) {
        try { cancel.call(video, frameId); } catch { /* déjà annulé */ }
      }
    },
  };
};

/**
 * Capture `video` en MediaStream : native si le navigateur sait, sinon canvas +
 * Web Audio. Rend null si rien ne peut être capturé.
 *
 * Chaque appel rend des pistes NEUVES : les appelants arrêtent les anciennes
 * (`oldTrack.stop()`) au moment de la reprise d'un flux.
 */
export const captureMediaElement = (video: HTMLVideoElement): MediaStream | null => {
  if (hasNativeCapture(video)) {
    return (video as any).captureStream?.() ?? (video as any).mozCaptureStream?.() ?? null;
  }

  let cap = captures.get(video);
  if (!cap) {
    const created = startCanvas(video);
    if (!created) return null;
    cap = created;
    captures.set(video, cap);
    liveCaptures++;
  }

  const vTrack = cap.canvas.captureStream(FALLBACK_FPS).getVideoTracks()[0];
  if (!vTrack) return null;
  const stream = new MediaStream([vTrack]);

  // Audio : un nœud de destination neuf à chaque capture (l'ancien est
  // débranché, ses pistes sont arrêtées par l'appelant) ; la source, elle, ne
  // peut être créée qu'UNE fois par élément.
  const ctx = getAudioContext();
  if (ctx) {
    try {
      if (!cap.audioSource) cap.audioSource = ctx.createMediaElementSource(video);
      try { cap.audioSource.disconnect(); } catch { /* rien de branché */ }
      cap.audioDest = ctx.createMediaStreamDestination();
      cap.audioSource.connect(cap.audioDest);
      cap.audioDest.stream.getAudioTracks().forEach(t => stream.addTrack(t));
    } catch (err) {
      console.warn('[mediaElementCapture] audio Web Audio indisponible, flux vidéo seul :', err);
    }
  }

  console.warn(
    `[mediaElementCapture] captureStream() absent — repli canvas ${cap.canvas.width}x${cap.canvas.height}` +
    ` + Web Audio (contexte ${ctx?.state ?? 'absent'})`
  );
  return stream;
};

/** À appeler quand l'élément est abandonné : arrête la boucle de dessin. */
export const stopMediaElementCapture = (video: HTMLVideoElement): void => {
  const cap = captures.get(video);
  if (!cap) return;
  cap.stopLoop();
  try { cap.audioSource?.disconnect(); } catch { /* déjà débranché */ }
  captures.delete(video);
  liveCaptures--;
};
